import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import phonesMockupDesktop from '@/static/assets/vets/phones-mockup-desktop.svg';
import phonesMockupMobile from '@/static/assets/vets/phones-mockup-mobile.svg';
import { Box, Flex, font, preventOneWordInLastLine } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import type React from 'react';
import styled from 'styled-components';

const StyledSectionTitle = styled(SectionTitle)`
  font-size: 2.8rem;
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 4.4rem;
  `}
`;

const StyledSectionParagraph = styled(SectionParagraph)`
  font-size: 2rem;
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 1.8rem;
  `}
`;

const Wrapper = styled(Section)`
  padding: 60px 20px 0;
  ${MEDIA_QUERIES.tabletAndDesktop`
    padding: 100px 20px 0;
  `}
`;

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  width: 100%;
`;

const PhoneContainer = styled(Flex)`
  position: relative;
  margin-top: 140px;
  justify-content: center;
  align-items: center;
  width: 378px;

  ${MEDIA_QUERIES.desktop`
    margin-top: 60px;
    width: 900px;
  `}
`;

const PhoneImageContainer = styled.div`
  position: relative;
  width: 378px;
  min-width: 378px;
  max-width: 378px;
  height: 256px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: 485px;
    min-width: 485px;
    max-width: 485px;
    height: 390px;
  `}
`;

const PhoneImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-image: url(${phonesMockupMobile.src});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;

  ${MEDIA_QUERIES.tabletAndDesktop`
    background-image: url(${phonesMockupDesktop.src});
  `}
`;

const Explanation = styled(Box)`
  position: absolute;
  top: -30px;
  transform: translateY(-100%);
  text-align: center;
  ${font('main', {
    fontSize: '1.6rem',
    lineHeight: '1.25',
  })}
  ${MEDIA_QUERIES.tablet`
    ${font('main', {
      fontSize: '2rem',
      lineHeight: '1.25',
    })}
  `}
  ${MEDIA_QUERIES.desktop`
    display: none;
  `}

  b {
    font-weight: 800;
  }
`;

const LeftExplanation = styled(Explanation)`
  left: 53px;
  top: -8px;
  max-width: 137px;

  ${MEDIA_QUERIES.tablet`
    left: 31px;
    top: -16px;
    max-width: 142px;
  `}
`;

const RightExplanation = styled(Explanation)`
  right: 50px;
  top: 40px;
  max-width: 160px;

  ${MEDIA_QUERIES.tablet`
    right: 0;
    top: 64px;
  `}
`;

const Callout = styled(Flex)`
  flex-direction: column;
  text-align: center;
  max-width: 180px;
  position: absolute;
  align-items: center;
  display: none;

  ${MEDIA_QUERIES.desktop`
    display: flex;
  `}
`;

const LeftCallout = styled(Callout)`
  right: 100%;
  top: 100px;
  margin-right: 20px;
  min-width: 175px;
`;

const RightCallout = styled(Callout)`
  left: 100%;
  top: 60px;
  margin-left: 20px;
  min-width: 200px;
`;

const CalloutTitle = styled.span`
  ${font('main', { fontSize: '2.4rem', fontWeight: 800 })};
`;

const CalloutText = styled.span`
  ${font('main', { fontSize: '2.4rem' })};
  white-space: pre-line;
`;

const CalloutBold = styled.span`
  ${font('main', { fontSize: '2.4rem', fontWeight: 800 })};
`;

const ArrowRightSvg = styled.svg`
  margin-top: 12px;
  margin-left: 80px;
  width: 27px;
  height: 48px;
`;

const ArrowRight: React.FC = () => (
  <ArrowRightSvg viewBox="0 0 27 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M23.0781 41.7947C7.14813 35.2869 -0.755296 18.4721 1.85429 1.36499"
      stroke="#4A4A4A"
      strokeWidth="2.7294"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20.6169 33.0417L25.1926 42.5812"
      stroke="#4A4A4A"
      strokeWidth="2.7294"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.7702 45.9462L25.1208 42.8221"
      stroke="#4A4A4A"
      strokeWidth="2.7294"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </ArrowRightSvg>
);

const ArrowLeftSvg = styled.svg`
  margin-top: 12px;
  margin-right: 80px;
  width: 27px;
  height: 38px;
`;

const ArrowLeft: React.FC = () => (
  <ArrowLeftSvg viewBox="0 0 27 38" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.47949 32.698C19.4095 27.6545 27.3129 14.623 24.7033 1.36499"
      stroke="#4A4A4A"
      strokeWidth="2.7294"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.94073 25.9144L1.36503 33.3075"
      stroke="#4A4A4A"
      strokeWidth="2.7294"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.78744 35.9153L1.4368 33.4942"
      stroke="#4A4A4A"
      strokeWidth="2.7294"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </ArrowLeftSvg>
);

export interface CustomersLoveSectionProps {
  title: string;
  subtitle: string;
  leftCallout: {
    title: string;
    text: string;
  };
  rightCallout: {
    prefix: string;
    bold: string;
  };
}

export const CustomersLoveSection: React.FC<CustomersLoveSectionProps> = ({
  title,
  subtitle,
  leftCallout,
  rightCallout,
}) => {
  return (
    <Wrapper>
      <ContentWrapper>
        <Flex flexDirection="column" gap="20px" alignItems="center">
          <StyledSectionTitle>{title}</StyledSectionTitle>
          <StyledSectionParagraph>{subtitle}</StyledSectionParagraph>
        </Flex>

        <PhoneContainer>
          <LeftExplanation>
            <b>{preventOneWordInLastLine(leftCallout.title)}</b>
            <br />
            <span>{preventOneWordInLastLine(leftCallout.text)}</span>
          </LeftExplanation>

          <PhoneImageContainer>
            <LeftCallout>
              <CalloutTitle>{preventOneWordInLastLine(leftCallout.title)}</CalloutTitle>
              <CalloutText>{preventOneWordInLastLine(leftCallout.text)}</CalloutText>
              <ArrowRight />
            </LeftCallout>

            <PhoneImageWrapper aria-label="Lemonade app showing ratings and claims" />

            <RightCallout>
              <CalloutText>{preventOneWordInLastLine(rightCallout.prefix)}</CalloutText>
              <CalloutBold>{preventOneWordInLastLine(rightCallout.bold)}</CalloutBold>
              <ArrowLeft />
            </RightCallout>
          </PhoneImageContainer>

          <RightExplanation>
            <span>{preventOneWordInLastLine(rightCallout.prefix)}</span>
            <br />
            <b>{preventOneWordInLastLine(rightCallout.bold)}</b>
          </RightExplanation>
        </PhoneContainer>
      </ContentWrapper>
    </Wrapper>
  );
};
